/**
 * Cloud Operations — System Workflow Catalog.
 *
 * Reads /api/v1/cloud-operations/workflows and renders the catalog. Each row
 * is a standalone_system workflow registered by SystemDeploymentWorkflowLoader.
 *
 * Per design doc §8 (catalog spec) and Mockup §"System workflow catalog".
 * Canvas read view (per-workflow drill-in) lands as W2.7.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { listSystemWorkflows, getSystemWorkflow } from '../../services/cloudOperationsApi';

const T = {
  ink:        '#0a1d44',
  text:       '#1a1f2b',
  text2:      '#5b6b7e',
  text3:      '#8896a8',
  stroke:     '#d4dbe5',
  stroke2:    '#e8edf3',
  bgCard:     '#ffffff',
  bgTint:     '#f7f9fc',
  bgAlt:      '#eef2f8',
  success:    '#2e7d4f',
  successBg:  '#e7f4ec',
  warn:       '#c58b00',
  warnBg:     '#fff4d9',
  danger:     '#c0392b',
  dangerBg:   '#fbe9e7',
  info:       '#1f5fa6',
  infoBg:     '#e8f0fb',
};

export default function SystemWorkflowCatalogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCloud, setFilterCloud] = useState('all');
  const [search, setSearch] = useState('');
  const [drillRow, setDrillRow] = useState(null);
  const [drillDetail, setDrillDetail] = useState(null);

  useEffect(() => {
    setLoading(true);
    listSystemWorkflows()
      .then((d) => { setItems(d?.items || []); setLoading(false); })
      .catch((e) => { setError(e?.message || String(e)); setLoading(false); });
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((w) => {
      const slug = (w.slug || '').toLowerCase();
      const name = (w.name || '').toLowerCase();
      const cloud = inferCloud(w);
      if (filterCloud !== 'all' && cloud !== filterCloud) return false;
      if (q && !name.includes(q) && !slug.includes(q)) return false;
      return true;
    });
  }, [items, search, filterCloud]);

  function inferCloud(w) {
    const slug = w.slug || '';
    if (slug.startsWith('gcp.'))   return 'gcp';
    if (slug.startsWith('aws.'))   return 'aws';
    if (slug.startsWith('azure.')) return 'azure';
    return 'agnostic';
  }

  async function onOpen(w) {
    setDrillRow(w);
    setDrillDetail(null);
    try {
      const d = await getSystemWorkflow(w.id);
      setDrillDetail(d);
    } catch (e) {
      setDrillDetail({ error: e?.message || String(e) });
    }
  }

  return (
    <div>
      <FilterBar
        search={search} onSearchChange={setSearch}
        filterCloud={filterCloud} onCloudChange={setFilterCloud}
        total={items.length} shown={filtered.length}
      />

      {error   && <Banner color={T.danger} bg={T.dangerBg}>Could not load catalog: {error}</Banner>}
      {loading && <Banner color={T.info}   bg={T.infoBg}>Loading catalog…</Banner>}
      {!loading && !error && (
        filtered.length === 0
          ? <Empty />
          : <CatalogTable rows={filtered} onOpen={onOpen} />
      )}

      <Slideout open={!!drillRow} onClose={() => { setDrillRow(null); setDrillDetail(null); }}>
        <WorkflowDetailPanel row={drillRow} detail={drillDetail} />
      </Slideout>
    </div>
  );
}

function FilterBar({ search, onSearchChange, filterCloud, onCloudChange, total, shown }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
      <input
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search by name or slug…"
        style={{
          flex: 1, minWidth: 240, padding: '7px 12px',
          border: `1px solid ${T.stroke}`, borderRadius: 4, fontSize: 13,
        }}
      />
      <select value={filterCloud} onChange={(e) => onCloudChange(e.target.value)} style={selSm}>
        <option value="all">All clouds</option>
        <option value="gcp">GCP</option>
        <option value="aws">AWS</option>
        <option value="azure">Azure</option>
        <option value="agnostic">Cloud-agnostic</option>
      </select>
      <div style={{ color: T.text2, fontSize: 12 }}>
        {shown} of {total}
      </div>
    </div>
  );
}

function Empty() {
  return (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 6,
      padding: 40, textAlign: 'center', color: T.text3, fontStyle: 'italic',
    }}>
      No System Deployment Workflows registered yet. Workflow JSONs in
      <code style={{ marginLeft: 4 }}>backend/src/main/resources/system-workflows/</code> are
      loaded on application start. Confirm SystemDeploymentWorkflowLoader ran cleanly in the boot logs.
    </div>
  );
}

function CatalogTable({ rows, onOpen }) {
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 6 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: T.bgTint }}>
            <Th>Workflow</Th>
            <Th>Cloud</Th>
            <Th>Category</Th>
            <Th>Version</Th>
            <Th>Status</Th>
            <Th>Runbook §</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => (
            <tr key={w.id}
                onClick={() => onOpen(w)}
                style={{ borderTop: `1px solid ${T.stroke2}`, cursor: 'pointer' }}>
              <Td>
                <div style={{ fontWeight: 600, color: T.ink }}>{w.name}</div>
                {w.slug && <div style={{ fontSize: 11, color: T.text3 }}><code>{w.slug}</code></div>}
                {w.description && <div style={{ fontSize: 11, color: T.text2, marginTop: 2 }}>{w.description}</div>}
              </Td>
              <Td><CloudPill cloud={inferCloud(w)} /></Td>
              <Td>{w.category || '—'}</Td>
              <Td><code style={{ fontSize: 11 }}>{w.version_label || `v${w.version}`}</code></Td>
              <Td><StatusPill status={w.status} /></Td>
              <Td><span style={{ color: T.text2, fontSize: 11 }}>{w.runbook_section || '—'}</span></Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function inferCloud(w) {
  const slug = w.slug || '';
  if (slug.startsWith('gcp.'))   return 'gcp';
  if (slug.startsWith('aws.'))   return 'aws';
  if (slug.startsWith('azure.')) return 'azure';
  return 'agnostic';
}

function CloudPill({ cloud }) {
  const colors = {
    gcp:       { fg: '#1f5fa6', bg: '#ecf3f8', label: 'GCP' },
    aws:       { fg: '#7a4d00', bg: '#fff4d9', label: 'AWS' },
    azure:     { fg: '#1f5fa6', bg: '#ecf3f8', label: 'Azure' },
    agnostic:  { fg: T.text2,   bg: T.bgAlt,   label: 'agnostic' },
  };
  const c = colors[cloud] || colors.agnostic;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 600, color: c.fg, background: c.bg,
      border: `1px solid ${c.fg}33`,
    }}>{c.label}</span>
  );
}

function StatusPill({ status }) {
  const map = {
    active:     { fg: T.success, bg: T.successBg, label: 'Active' },
    draft:      { fg: T.warn,    bg: T.warnBg,    label: 'Draft' },
    deprecated: { fg: T.text3,   bg: T.bgAlt,     label: 'Deprecated' },
  };
  const c = map[status] || { fg: T.text2, bg: T.bgAlt, label: status || '—' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: 11, fontWeight: 600, color: c.fg, background: c.bg,
      border: `1px solid ${c.fg}33`,
    }}>{c.label}</span>
  );
}

// ─── Workflow detail panel (slideout body) ───────────────────────────────────
function WorkflowDetailPanel({ row, detail }) {
  if (!row) return null;
  const meta = detail?.definition?.spec_metadata || {};
  const nodes = detail?.definition?.nodes || [];
  const connections = detail?.definition?.connections || [];

  return (
    <div style={{ padding: 24, overflow: 'auto', height: '100%' }}>
      <div style={{ fontSize: 12, color: T.text3, marginBottom: 4 }}>
        System Deployment Workflow
      </div>
      <h2 style={{ margin: 0, fontSize: 18, color: T.ink, fontWeight: 700 }}>{row.name}</h2>
      {row.slug && <div style={{ fontSize: 12, color: T.text2, marginTop: 4 }}><code>{row.slug}</code></div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16, fontSize: 12 }}>
        <Detail label="Version">{row.version_label || `v${row.version}`}</Detail>
        <Detail label="Category">{row.category || '—'}</Detail>
        <Detail label="Status"><StatusPill status={row.status} /></Detail>
        <Detail label="Kind"><code style={{ fontSize: 11 }}>{row.kind}</code></Detail>
        <Detail label="Runbook">{row.runbook_section || meta?.metadata?.runbook_section || '—'}</Detail>
        <Detail label="Criticality">{row.criticality || meta?.metadata?.criticality || '—'}</Detail>
      </div>

      {!detail && <div style={{ marginTop: 24, color: T.text2, fontSize: 12 }}>Loading definition…</div>}
      {detail?.error && <Banner color={T.danger} bg={T.dangerBg}>{detail.error}</Banner>}

      {detail && !detail.error && (
        <>
          <Section title={`Nodes (${nodes.length})`}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ color: T.text2 }}>
                <th style={cellHead}>id</th><th style={cellHead}>type</th>
                <th style={cellHead}>connector / skill</th><th style={cellHead}>name</th>
              </tr></thead>
              <tbody>
                {nodes.map((n) => (
                  <tr key={n.id} style={{ borderTop: `1px solid ${T.stroke2}` }}>
                    <td style={cell}><code style={{ fontSize: 11 }}>{n.id}</code></td>
                    <td style={cell}>{n.type}</td>
                    <td style={cell}>
                      {n.connector && <code style={{ fontSize: 11 }}>{n.connector}.{n.skill}</code>}
                    </td>
                    <td style={cell}>{n.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title={`Edges (${connections.length})`}>
            <pre style={preStyle}>
{connections.map((e, i) => `${e.from || e.fromNodeId} → ${e.to || e.toNodeId}`).join('\n') || '(none)'}
            </pre>
          </Section>

          {meta && (
            <Section title="Spec metadata">
              <pre style={preStyle}>{JSON.stringify(meta, null, 2)}</pre>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ fontSize: 13, color: T.ink, fontWeight: 600, margin: '0 0 8px' }}>{title}</h3>
      {children}
    </div>
  );
}
function Detail({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: 'uppercase', color: T.text3, letterSpacing: '.06em' }}>{label}</div>
      <div style={{ marginTop: 2 }}>{children}</div>
    </div>
  );
}

// ─── style primitives ───────────────────────────────────────────────────────
const selSm = {
  padding: '6px 10px', border: `1px solid ${T.stroke}`,
  borderRadius: 4, fontSize: 12, background: '#fff', color: T.text,
};
const cellHead = {
  textAlign: 'left', padding: '6px 8px', fontSize: 10,
  textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600,
  borderBottom: `1px solid ${T.stroke}`,
};
const cell = { padding: '6px 8px', verticalAlign: 'top' };
const preStyle = {
  fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11,
  background: T.bgTint, border: `1px solid ${T.stroke}`, borderRadius: 4,
  padding: '8px 12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  maxHeight: 320, overflow: 'auto',
};

function Th({ children }) {
  return <th style={{
    textAlign: 'left', padding: '10px 12px', fontSize: 11,
    textTransform: 'uppercase', letterSpacing: '.06em',
    color: T.text2, fontWeight: 600, borderBottom: `1px solid ${T.stroke}`,
  }}>{children}</th>;
}
function Td({ children }) {
  return <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>{children}</td>;
}
function Banner({ color, bg, children }) {
  return <div style={{
    background: bg, borderLeft: `3px solid ${color}`, padding: '10px 14px',
    borderRadius: 4, color, fontSize: 13, margin: '12px 0',
  }}>{children}</div>;
}

function Slideout({ open, onClose, children }) {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(10,29,68,.18)', zIndex: 30,
      }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 640, maxWidth: '90vw',
        background: T.bgCard, borderLeft: `1px solid ${T.stroke}`, zIndex: 31,
        boxShadow: '-12px 0 32px rgba(10,29,68,.12)',
      }}>{children}</div>
    </>
  );
}
